/**
 * Drive times after a move — parity with the command centre (3.2).
 *
 * Nothing on the server recomputes drive time when a visit is re-timed or moved
 * to another day. `updateVisitResourceObject` writes exactly the fields it is
 * given and no more; the paths that *do* recompute (`updateDriveTimesForTimespan`
 * and friends) are reachable only from calendar-block writes, shift writes and
 * cancellation. The command centre's answer is to price the day in the browser
 * and send the minutes with the move: `useVisitDragDrop` records a
 * `driveUpdatesNeeded` entry per shift a drag touched and calls
 * `recalculateDriveTimesFromState` for each, then saves every row the patch
 * changed. This module does the same thing for the demo.
 *
 * `recalculateDriveTimesFromState` is imported **as it is** — it takes its fetch
 * function as a parameter, so it was never tied to command's hook, and no
 * product file changes here. What this module adds around it is only the part
 * the hook got from React: the day to price (read through `fetchCommandDay`,
 * the same `CommandIndexCombinedData` read **Optimize** and **View Route**
 * make), the move applied to that day before it is priced, and the choice of
 * which shifts to price.
 *
 * Two things to keep in mind when reading it:
 *
 * - **The whole shift is repriced, not the moved visit.** Drive belongs to the
 *   stop it arrives at, so moving one visit changes the `interVisitDriveTime` of
 *   the stop after it and can change the `endOfShiftDriveTime` of the day's last
 *   one. The command centre writes every row the patch changed
 *   (`findModifiedResourceIds`), and so does the caller of this module.
 * - **A cross-day move touches two shifts**, the one it left and the one it
 *   lands on, and each is one call to the paid `GetSimpleDriveTime` path. A move
 *   inside one shift is one call.
 *
 * Cost: one `CommandIndexCombinedData` read per distinct day involved (free),
 * and one `GetSimpleDriveTime` per distinct shift (paid, Google). Nothing here
 * runs on render or on a poll — the caller runs it once per completed move.
 */
import { recalculateDriveTimesFromState } from "~/hooks/useVisitDragDrop";
import type { LocalCalendarBlock, LocalShift, LocalVisitResource } from "~/hooks/visitDragDropReducer";
import type { OrgConfig } from "~/providers/OrgConfigProvider";
import type { CommandIndexCombinedDataQuery, GetSimpleDriveTimeQueryVariables } from "~/graphql/__generated__/graphql";
import { addDaysToKey, dateKeyStart, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { fetchCommandDay, simpleDriveTime, type DemoClient, type DriveMinutes } from "./mutations";

/** One visit resource about to change position, with the position it is leaving. */
export interface PlannedMove {
  visitResourceId: number;
  /** Where it is now. Undefined when the horizon does not know — the source shift is then not priced. */
  fromProviderId?: number;
  fromStartIso: string;
  fromEndIso: string;
  /** The clinician it lands on. Undefined means it stays where it is. */
  toProviderId?: number;
  toStartIso: string;
  toEndIso: string;
}

/**
 * One calendar block about to change position. A block with an address is a stop
 * on the route, so a day priced with the block where the server still has it is
 * priced against a route nobody is going to drive. The product never needs this —
 * it reprices a block's shift server-side inside the write — but My day prices a
 * **staged** day, before anything is written, and has to place the block itself.
 */
export interface PlannedBlockMove {
  calendarBlockId: number;
  toStartIso: string;
  toEndIso: string;
}

export interface DriveTimePlan {
  /** New minutes by visit resource id: the moved rows and every neighbour whose drive changed. */
  next: Record<number, DriveMinutes>;
  /** What those same rows read before the move; a row whose minutes did not change is not written. */
  previous: Record<number, DriveMinutes>;
  /** Shift ids priced — one paid `GetSimpleDriveTime` call each. */
  pricedShiftIds: string[];
}

export function emptyDriveTimePlan(): DriveTimePlan {
  return { next: {}, previous: {}, pricedShiftIds: [] };
}

/** `recalculateDriveTimesFromState`'s fifth parameter, named without importing command's hook type. */
type FetchDriveTimes = Parameters<typeof recalculateDriveTimesFromState>[4];

/**
 * The demo's own drive-time read, in the shape the command centre's function
 * expects. `recalculateDriveTimesFromState` awaits this and reads
 * `data.simpleDriveTime` and nothing else, so one query is the whole contract;
 * the cast covers the rest of Apollo's lazy-query result, which it never touches.
 */
function driveTimeFetcher(client: DemoClient): FetchDriveTimes {
  const fetch = async (options?: { variables?: GetSimpleDriveTimeQueryVariables }) => {
    if (!options?.variables) throw new Error("drive-time fetch called with no variables");
    return { data: { simpleDriveTime: await simpleDriveTime(client, options.variables) } };
  };
  return fetch as unknown as FetchDriveTimes;
}

/** The query hands `visit` back nullable; command's local shapes do not. */
function nonEmptyVisit(visitResource: CommandIndexCombinedDataQuery["visitResource"][number]): visitResource is LocalVisitResource {
  return visitResource.visit !== null;
}

/**
 * Every time on every row through one format. `recalculateDriveTimesFromState`
 * decides which resources belong to a shift by comparing the strings
 * (`resource.startTime >= shift.startTime`), so a row this module rewrites with
 * `Date.toISOString()` and a row straight off the query have to be spelled the
 * same way or the comparison is nonsense.
 */
function isoOf(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toISOString();
}

function driveMinutesOf(row: { startOfShiftDriveTime?: number | null; interVisitDriveTime?: number | null; endOfShiftDriveTime?: number | null }): DriveMinutes {
  return {
    startOfShiftDriveTime: row.startOfShiftDriveTime ?? 0,
    interVisitDriveTime: row.interVisitDriveTime ?? 0,
    endOfShiftDriveTime: row.endOfShiftDriveTime ?? 0,
  };
}

function sameMinutes(a: DriveMinutes, b: DriveMinutes): boolean {
  return a.startOfShiftDriveTime === b.startOfShiftDriveTime && a.interVisitDriveTime === b.interVisitDriveTime && a.endOfShiftDriveTime === b.endOfShiftDriveTime;
}

/**
 * The shift a stop sits in, by the same rule the command centre's function uses
 * to gather a shift's resources: same provider, contained in the shift's window.
 * A canceled shift is not a day (3.1's call-out cancels one), so it is skipped.
 */
function shiftCovering(shifts: LocalShift[], providerId: number | undefined, startIso: string, endIso: string): LocalShift | undefined {
  if (providerId === undefined) return undefined;
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  return shifts.find((shift) => shift.provider?.id === providerId && shift.status !== "canceled" && new Date(shift.startTime).getTime() <= start && new Date(shift.endTime).getTime() >= end);
}

function dedupeById<T extends { id: number }>(rows: T[]): T[] {
  const byId = new Map<number, T>();
  for (const row of rows) if (!byId.has(row.id)) byId.set(row.id, row);
  return [...byId.values()];
}

/**
 * Price every shift the moves touch and say what changed.
 *
 * Called **before** the move is written, so the day that comes back still
 * carries the old positions — which is where `previous` comes from — and the
 * moves are applied to it in memory, which is exactly what the command centre
 * prices: its own working copy, after the drag and before the save.
 */
export async function planDriveTimesForMoves(client: DemoClient, orgConfig: OrgConfig, timeZone: string, moves: PlannedMove[], blockMoves: PlannedBlockMove[] = []): Promise<DriveTimePlan> {
  if (moves.length === 0 && blockMoves.length === 0) return emptyDriveTimePlan();

  const dayKeys = [
    ...new Set([
      ...moves.flatMap((move) => [toDateKey(new Date(move.fromStartIso), timeZone), toDateKey(new Date(move.toStartIso), timeZone)]),
      ...blockMoves.map((move) => toDateKey(new Date(move.toStartIso), timeZone)),
    ]),
  ];
  const days = await Promise.all(
    dayKeys.map((day) =>
      fetchCommandDay(client, {
        filterStartTime: dateKeyStart(day, timeZone).toISOString(),
        filterEndTime: dateKeyStart(addDaysToKey(day, 1), timeZone).toISOString(),
      }),
    ),
  );

  const dbResources = dedupeById(days.flatMap((day) => day.visitResource.filter(nonEmptyVisit))).map((resource) => ({
    ...resource,
    startTime: isoOf(resource.startTime),
    endTime: isoOf(resource.endTime),
  }));
  const blockMovesById = new Map(blockMoves.map((move) => [move.calendarBlockId, move]));
  const dbBlocks = dedupeById(days.flatMap((day) => day.calendarBlock)).map((block) => ({
    ...block,
    startTime: isoOf(block.startTime),
    endTime: isoOf(block.endTime),
  })) as LocalCalendarBlock[];
  const blocks: LocalCalendarBlock[] = dbBlocks.map((block) => {
    const move = blockMovesById.get(block.id);
    return move ? { ...block, startTime: isoOf(move.toStartIso), endTime: isoOf(move.toEndIso) } : block;
  });
  const shifts = dedupeById(days.flatMap((day) => day.shift)).map((shift) => ({
    ...shift,
    startTime: isoOf(shift.startTime),
    endTime: isoOf(shift.endTime),
  })) as LocalShift[];

  const movesById = new Map(moves.map((move) => [move.visitResourceId, move]));
  const moved = dbResources.map((resource) => {
    const move = movesById.get(resource.id);
    if (!move) return resource;
    return { ...resource, providerId: move.toProviderId ?? resource.providerId, startTime: isoOf(move.toStartIso), endTime: isoOf(move.toEndIso) };
  });

  // The shift it left and the shift it lands on, deduplicated: a re-time inside
  // one day is one shift and one call.
  const shiftIds = new Set<string>();
  for (const move of moves) {
    const from = shiftCovering(shifts, move.fromProviderId, move.fromStartIso, move.fromEndIso);
    const to = shiftCovering(shifts, move.toProviderId ?? move.fromProviderId, move.toStartIso, move.toEndIso);
    if (from) shiftIds.add(from.id.toString());
    if (to) shiftIds.add(to.id.toString());
  }
  // A block that moves inside a day changes the shift's route even when no visit
  // moved with it, so its shift is priced too — the one it sat in and the one it
  // lands in, which for a re-time on one day are the same shift and one call.
  for (const move of blockMoves) {
    const block = dbBlocks.find((b) => b.id === move.calendarBlockId);
    if (!block) continue;
    const from = shiftCovering(shifts, block.providerId ?? undefined, block.startTime, block.endTime);
    const to = shiftCovering(shifts, block.providerId ?? undefined, move.toStartIso, move.toEndIso);
    if (from) shiftIds.add(from.id.toString());
    if (to) shiftIds.add(to.id.toString());
  }
  if (shiftIds.size === 0) return emptyDriveTimePlan();

  const requestedAt = Date.now();
  const patches = await Promise.all([...shiftIds].map((shiftId) => recalculateDriveTimesFromState(shiftId, moved, blocks, shifts, driveTimeFetcher(client), orgConfig, requestedAt)));

  const dbById = new Map(dbResources.map((resource) => [resource.id, resource]));
  const plan: DriveTimePlan = { next: {}, previous: {}, pricedShiftIds: [...shiftIds] };
  for (const patch of patches) {
    if (!patch) continue;
    for (const [eventKey, minutes] of Object.entries(patch.patch)) {
      // A block's key is `calendarBlock-<id>`: the product recomputes a block's
      // own drive on its own writes, and neither My week nor My day draws it.
      const id = Number(eventKey);
      if (!Number.isInteger(id)) continue;
      const before = dbById.get(id);
      if (!before) continue;
      const after = driveMinutesOf(minutes);
      const wasMoved = movesById.has(id);
      const previous = driveMinutesOf(before);
      if (!wasMoved && sameMinutes(previous, after)) continue;
      plan.next[id] = after;
      plan.previous[id] = previous;
    }
  }
  return plan;
}
